import React, { useEffect, useRef, useState } from "react";
import { Share2, Download, X, Loader2, Copy } from "lucide-react";
import { buildStreakCanvas, buildStreakBlob } from "../lib/shareStreakImage";
import { toast } from "sonner";

/**
 * Modal that previews the streak-card image and exposes 3 actions:
 * Compartilhar (Web Share), Baixar JPG, Copiar texto.
 */
export default function ShareStreakModal({ open, onClose, days }) {
  const canvasContainerRef = useRef(null);
  const [building, setBuilding] = useState(false);
  const [acting, setActing] = useState(false);

  useEffect(() => {
    if (!open || days == null) return;
    let cancelled = false;
    setBuilding(true);
    (async () => {
      try {
        const canvas = await buildStreakCanvas({ days });
        if (cancelled) return;
        canvas.style.width = "100%";
        canvas.style.height = "auto";
        canvas.style.display = "block";
        canvas.style.borderRadius = "16px";
        const el = canvasContainerRef.current;
        if (el) { el.innerHTML = ""; el.appendChild(canvas); }
      } finally {
        if (!cancelled) setBuilding(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, days]);

  if (!open || days == null) return null;

  const fileName = `ofensiva-${days}-dias.jpg`;
  const captionText =
    `${days} ${days === 1 ? "dia seguido" : "dias seguidos"} com Deus, junte-se a mim em app.cemedeiros.com.br`;

  const downloadBlob = (blob) => {
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => { URL.revokeObjectURL(a.href); a.remove(); }, 1000);
  };

  const handleShare = async () => {
    if (acting) return;
    setActing(true);
    try {
      const blob = await buildStreakBlob({ days });
      const file = new File([blob], fileName, { type: "image/jpeg" });
      const payload = {
        title: "Minha ofensiva",
        text: captionText,
        files: [file],
      };
      if (
        typeof navigator !== "undefined" &&
        typeof navigator.canShare === "function" &&
        navigator.canShare({ files: [file] }) &&
        typeof navigator.share === "function"
      ) {
        await navigator.share(payload);
        toast.success("Compartilhado!");
        onClose?.();
      } else {
        downloadBlob(blob);
        toast.success("Imagem baixada, anexe nos Stories.");
      }
    } catch (err) {
      if (err && err.name === "AbortError") return;
      toast.error("Não foi possível compartilhar.");
    } finally {
      setActing(false);
    }
  };

  const handleDownload = async () => {
    if (acting) return;
    setActing(true);
    try {
      const blob = await buildStreakBlob({ days });
      downloadBlob(blob);
      toast.success("Imagem baixada!");
    } catch {
      toast.error("Não foi possível gerar a imagem.");
    } finally {
      setActing(false);
    }
  };

  const handleCopyText = async () => {
    try {
      await navigator.clipboard.writeText(captionText);
      toast.success("Texto copiado.");
    } catch {
      toast.error("Não foi possível copiar.");
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="share-streak-modal"
    >
      <div
        className="bg-white rounded-3xl max-w-md w-full max-h-[92vh] overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between px-5 py-4 border-b border-border">
          <div>
            <p className="text-[10px] uppercase tracking-[0.3em] text-gold">Compartilhar ofensiva</p>
            <p className="text-sm text-navy/65 mt-0.5">{days} {days === 1 ? "dia" : "dias"} seguidos com Deus</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="share-streak-close"
            aria-label="Fechar"
            className="w-9 h-9 rounded-full text-navy/55 hover:bg-[hsl(var(--parchment))] flex items-center justify-center"
          >
            <X className="w-4 h-4" />
          </button>
        </header>

        <div className="p-5 space-y-4">
          <div
            ref={canvasContainerRef}
            data-testid="share-streak-preview"
            className="aspect-square w-full bg-navy rounded-2xl flex items-center justify-center text-white/60 text-sm"
          >
            {building && (
              <div className="inline-flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" /> Gerando imagem…
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-2.5">
            <button
              type="button"
              onClick={handleShare}
              disabled={building || acting}
              data-testid="share-streak-share"
              className="inline-flex items-center justify-center gap-2 bg-navy text-white hover:bg-gold hover:text-navy disabled:opacity-50 px-5 py-3 rounded-full text-[11px] uppercase tracking-widest transition-colors"
            >
              {acting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />}
              Compartilhar
            </button>
            <button
              type="button"
              onClick={handleDownload}
              disabled={building || acting}
              data-testid="share-streak-download"
              className="inline-flex items-center justify-center gap-2 bg-white border border-border text-navy hover:border-gold/60 disabled:opacity-50 px-5 py-3 rounded-full text-[11px] uppercase tracking-widest transition-colors"
            >
              <Download className="w-4 h-4" />
              Baixar JPG
            </button>
          </div>

          <button
            type="button"
            onClick={handleCopyText}
            data-testid="share-streak-copy-text"
            className="w-full inline-flex items-center justify-center gap-2 text-[11px] uppercase tracking-widest text-navy/60 hover:text-gold py-2 transition-colors"
          >
            <Copy className="w-3.5 h-3.5" />
            Copiar apenas o texto
          </button>

          <p className="text-[11px] text-navy/45 text-center leading-relaxed pt-1">
            A imagem é gerada localmente, sem servidor.
          </p>
        </div>
      </div>
    </div>
  );
}
